import React from "react";
import { MAX_MISTAKES } from "../../lib/constants";
import { PuzzleDataContext } from "../PuzzleDataProvider";
import {
  loadGameStateFromLocalStorage,
  saveGameStateToLocalStorage,
} from "../../lib/local-storage";
import {
  isGameDataEquivalent,
  isGuessesFromGame,
} from "../../lib/game-helpers";
import { doArraysHaveSameValues } from "../../lib/utils";
export const GameStatusContext = React.createContext();

function getStateFromQuery(gameData) {
  if (typeof window === "undefined") return null;
  const state = new URLSearchParams(window.location.search).get("state");
  if (!state) return null;

  const buildSolved = (slice) =>
    slice.map(({ category, words, difficulty }) => ({
      category,
      words,
      difficulty,
    }));

  switch (state) {
    case "fresh":
      return { submittedGuesses: [], solvedGameData: [] };
    case "partial": {
      const solved = buildSolved(gameData.slice(0, 2));
      return {
        submittedGuesses: solved.map((d) => d.words),
        solvedGameData: solved,
      };
    }
    case "won": {
      const solved = buildSolved(gameData);
      return {
        submittedGuesses: solved.map((d) => d.words),
        solvedGameData: solved,
      };
    }
    case "outOfMistakes": {
      const cs = gameData[0].words.length;
      const wrongGuesses = [];
      for (let i = 0; i < 4; i++) {
        const guess = [];
        for (let j = 0; j < cs; j++) {
          guess.push(gameData[(i + j) % gameData.length].words[j % cs]);
        }
        wrongGuesses.push(guess);
      }
      return { submittedGuesses: wrongGuesses, solvedGameData: [] };
    }
    default:
      return null;
  }
}

function GameStatusProvider({ children }) {
  const { gameData } = React.useContext(PuzzleDataContext);
  const queryState = getStateFromQuery(gameData);
  const [submittedGuesses, setSubmittedGuesses] = React.useState(
    queryState?.submittedGuesses ?? []
  );
  const [solvedGameData, setSolvedGameData] = React.useState(() => {
    if (queryState) return queryState.solvedGameData;
    const loadedState = loadGameStateFromLocalStorage();
    console.log("checking game state!", {
      loadedState: loadedState,
      gd1: gameData,
      gd2: loadedState?.gameData,
    });
    if (!isGameDataEquivalent({ gd1: gameData, gd2: loadedState?.gameData })) {
      return [];
    }
    if (
      !isGuessesFromGame({
        gameData,
        submittedGuesses: loadedState?.submittedGuesses,
      })
    ) {
      return [];
    }
    if (Array.isArray(loadedState?.submittedGuesses)) {
      setSubmittedGuesses(loadedState.submittedGuesses);
    }

    if (Array.isArray(loadedState?.solvedGameData)) {
      return loadedState.solvedGameData;
    }
    return [];
  });

  const [isGameOver, setIsGameOver] = React.useState(false);
  const [isGameWon, setIsGameWon] = React.useState(false);
  const [guessCandidate, setGuessCandidate] = React.useState([]);

  const numMistakesUsed = submittedGuesses.length - solvedGameData.length;
  const isOutOfMistakes = numMistakesUsed >= MAX_MISTAKES;

  // use effect to check if game is won
  React.useEffect(() => {
    if (solvedGameData.length === gameData.length) {
      setIsGameOver(true);
      setIsGameWon(true);
    }
    const gameState = { submittedGuesses, solvedGameData, gameData };
    saveGameStateToLocalStorage(gameState);
  }, [solvedGameData]);

  React.useEffect(() => {
    const gameState = { submittedGuesses, solvedGameData, gameData };
    saveGameStateToLocalStorage(gameState);
  }, [submittedGuesses]);

  function resetGame() {
    setSubmittedGuesses([]);
    setSolvedGameData([]);
    setGuessCandidate([]);
    setIsGameOver(false);
    setIsGameWon(false);
  }

  function resetMistakes() {
    const solvedWordSets = solvedGameData.map((d) => d.words);
    setSubmittedGuesses(
      submittedGuesses.filter((guess) =>
        solvedWordSets.some((solvedWords) =>
          doArraysHaveSameValues(guess, solvedWords)
        )
      )
    );
    setGuessCandidate([]);
  }

  return (
    <GameStatusContext.Provider
      value={{
        isGameOver,
        isGameWon,
        isOutOfMistakes,
        resetGame,
        resetMistakes,
        numMistakesUsed,
        solvedGameData,
        setSolvedGameData,
        submittedGuesses,
        setSubmittedGuesses,
        guessCandidate,
        setGuessCandidate,
      }}
    >
      {children}
    </GameStatusContext.Provider>
  );
}

export default GameStatusProvider;
